import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ShadowDomContext } from './context'
import { useTheme } from '@strata-monorepo/shared-ui/utils/context/ThemeContext'

import styles from '../../styles/globals.css?inline'

interface ShadowDomWrapperProps {
  children: React.ReactNode
}

const ShadowDomWrapper: React.FC<ShadowDomWrapperProps> = ({ children }) => {
  const theme = useTheme()
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null)
  const [defaultPortalContainer, setDefaultPortalContainer] = useState<HTMLDivElement | null>(null)
  const [containerPortalContainer, setContainerPortalContainer] = useState<HTMLDivElement | null>(
    null,
  )

  const hostRef = useCallback(
    (hostNode: HTMLDivElement) => {
      if (hostNode && !shadowRoot) {
        // Check if shadow root already exists (for hot reload scenarios)
        let newShadowRoot: ShadowRoot

        if (hostNode.shadowRoot) {
          // Use existing shadow root
          newShadowRoot = hostNode.shadowRoot
        } else {
          // Create new shadow root
          try {
            newShadowRoot = hostNode.attachShadow({ mode: 'open' })
          } catch (error) {
            console.error('[ShadowDOM] Failed to create shadow root:', error)
            return
          }
        }

        // Clear existing content and setup fresh
        newShadowRoot.innerHTML = ''

        const styleEl = document.createElement('style')
        styleEl.textContent = styles
        newShadowRoot.appendChild(styleEl)

        // Default portal container - NO container queries (tooltips, popovers, etc.)
        const defaultPortalEl = document.createElement('div')
        newShadowRoot.appendChild(defaultPortalEl)

        // Container portal container - WITH container queries (dialogs, sheets, etc.)
        const containerPortalEl = document.createElement('div')
        containerPortalEl.className = 'strata:@container'
        newShadowRoot.appendChild(containerPortalEl)

        setShadowRoot(newShadowRoot)
        setDefaultPortalContainer(defaultPortalEl)
        setContainerPortalContainer(containerPortalEl)
      }
    },
    [shadowRoot],
  )

  useEffect(() => {
    if (shadowRoot?.host && theme) {
      // Find and remove any existing theme class
      const currentTheme = Array.from(shadowRoot.host.classList).find((c) => c.startsWith('theme-'))
      if (currentTheme) {
        shadowRoot.host.classList.remove(currentTheme)
      }
      // Add the new theme class from context
      shadowRoot.host.classList.add(theme)
    }
  }, [shadowRoot, theme])

  return (
    <div
      ref={hostRef}
      data-testid='grid'
    >
      {shadowRoot &&
        defaultPortalContainer &&
        containerPortalContainer &&
        createPortal(
          <ShadowDomContext.Provider
            value={{
              root: shadowRoot,
              portalContainer: defaultPortalContainer,
              containerPortalContainer,
            }}
          >
            {children}
          </ShadowDomContext.Provider>,
          shadowRoot,
        )}
    </div>
  )
}

export default ShadowDomWrapper
